import React from 'react';
import _ from 'lodash';
import { styled } from '@this/constants/themes';

import { Header, Icon, Date, Body } from '@this/components/reserve_trip/select/outline_item/outline_item.template';
import {
  BoxContent,
  Left,
  Segment,
  Name,
  Space
} from '@this/src/components/reserve_trip/select/transport_box/transport_box_foreign.template';
import {
  BoxPriceArea,
  BoxPrice
} from '@this/components/reserve_trip/select/transport_box/right/right_in_outline.template';
import { HotelNameDiv } from '@this/components/reserve_trip/select/hotel_box/hotel_box_parts';

import type OrderItem from '@this/domain/order_item';
import OrderItem2 from '@this/domain/order_item2';
import HotelElement2 from '@this/domain/hotel_element2';
import TransportElement2 from '@this/domain/transport_element2';
import TransportElementTerminal from '@this/domain/transport_element_terminal';
import TransportElementTerminal2 from '@this/domain/transport_element_terminal/transport_element_terminal2';
import { reportError } from '@this/lib/bugsnag';

interface Props {
  item: OrderItem | OrderItem2;
  icon: number;
}

interface State {
  item: OrderItem | OrderItem2;
  icon: number;
}

const isOrderItem2 = (item: OrderItem | OrderItem2): item is OrderItem2 => item instanceof OrderItem2;

const getOrderItemType = (item: OrderItem | OrderItem2) => {
  if (isOrderItem2(item)) {
    const element = item.elements[0];
    if (element instanceof HotelElement2) return 'hotel';
    if (element instanceof TransportElement2) return 'transport';
    return '';
  }
  return item.elementType();
};

const getHotelDateLabel = (item: OrderItem | OrderItem2) => {
  if (isOrderItem2(item)) {
    const element = item.elements.find(el => el instanceof HotelElement2) as HotelElement2 | undefined;
    const start = element?.checkinDate ? element.checkinDate.format('YYYY-MM-DD') : '';
    const end = element?.checkoutDate ? element.checkoutDate.format('YYYY-MM-DD') : '';
    return `宿泊：${start}〜${end}`;
  }
  const legacyItem = item as OrderItem;
  return `宿泊：${legacyItem.startDateStr()}〜${legacyItem.endDateStr()}`;
};

const getTransportDateLabel = (item: OrderItem | OrderItem2) => {
  if (isOrderItem2(item)) {
    const element = item.elements.find(el => el instanceof TransportElement2) as TransportElement2 | undefined;
    const from = element?.from;
    let date = '';
    if (from instanceof TransportElementTerminal) {
      date = from.dateStr();
    } else if (from instanceof TransportElementTerminal2) {
      date = from.dateStr;
    }
    return `交通機関：${date}`;
  }
  const legacyItem = item as OrderItem;
  return `交通機関：${legacyItem.startDateStr()}`;
};

const getOrderItemTotalPrice = (item: OrderItem | OrderItem2) => {
  if (isOrderItem2(item)) {
    return item.totalPrice;
  }
  const legacyItem = item as OrderItem;
  return legacyItem.totalPriceWithAll();
};

class OrderItemBlock extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      item: props.item,
      icon: props.icon
    };
  }

  render() {
    const { item, icon } = this.state;
    try {
      return (
        <div className="select-outline__item-block">
          {getOrderItemType(item) === 'hotel' ? (
            <>
              <Header key={0}>
                <Icon>{icon}</Icon>
                <Date>{getHotelDateLabel(item)}</Date>
              </Header>
              <Body key={1}>
                <Box>
                  <Left>
                    <HotelBody>
                      {isOrderItem2(item) ? (
                        <>
                          {item.elements
                            .filter(el => el instanceof HotelElement2)
                            .map(element => {
                              const hotel = element as HotelElement2;
                              const bedTypeText = hotel.bedTypeNoticeMessage || '';
                              return (
                                <React.Fragment key={hotel.id}>
                                  <HotelNameDiv>{hotel.name}</HotelNameDiv>
                                  <HotelBodyText>{hotel.planNameDescription}</HotelBodyText>
                                  <HotelBodyText>{hotel.roomTypeDescription}</HotelBodyText>
                                  <HotelBodyText style={bedTypeDisplayStyle}>{bedTypeText}</HotelBodyText>
                                  <HotelBodyText>{hotel.breakfastDescription}</HotelBodyText>
                                  <HotelBodyText>{hotel.refundableDescription}</HotelBodyText>
                                </React.Fragment>
                              );
                            })}
                        </>
                      ) : (
                        <>
                          <HotelNameDiv>{item.hotels[0].name}</HotelNameDiv>
                          <HotelBodyText>{item.hotels[0].planNameDescription()}</HotelBodyText>
                          <HotelBodyText>{item.hotels[0].roomTypeDescription()}</HotelBodyText>
                          <HotelBodyText style={bedTypeDisplayStyle}>
                            {item.hotels[0].bedTypeDescription().replace(/^\n/, '')}
                          </HotelBodyText>
                          <HotelBodyText>{item.hotels[0].breakfastDescription()}</HotelBodyText>
                          <HotelBodyText>{item.hotels[0].refundableDescription()}</HotelBodyText>
                        </>
                      )}
                    </HotelBody>
                  </Left>
                  <Right>
                    <BoxPriceArea>
                      <div>
                        <BoxPrice>{utils.formatPriceWithMark(getOrderItemTotalPrice(item))}</BoxPrice>
                      </div>
                    </BoxPriceArea>
                  </Right>
                </Box>
              </Body>
            </>
          ) : getOrderItemType(item) === 'transport' ? (
            <>
              <Header key={0}>
                <Icon>{icon}</Icon>
                <Date>{getTransportDateLabel(item)}</Date>
              </Header>
              <Body key={1} className="select-outline__item-block__body">
                <Box>
                  <Left>
                    {isOrderItem2(item)
                      ? item.elements
                          .filter(el => el instanceof TransportElement2)
                          .map((element, i) => {
                            const transport = element as TransportElement2;
                            const fromTime =
                              transport.from && 'description' in transport.from ? transport.from.description : '';
                            const toTime =
                              transport.to && 'description' in transport.to ? transport.to.description : '';
                            return (
                              <Segment key={i}>
                                <div className="flex">
                                  <Name>{transport.name}</Name>
                                </div>
                                <BoxContent>
                                  <Space>発</Space>
                                  <Space>{fromTime}</Space>
                                  <Space>{transport.from?.name}</Space>
                                </BoxContent>
                                <BoxContent>
                                  <Space>着</Space>
                                  <Space>{toTime}</Space>
                                  <Space>{transport.to?.name}</Space>
                                </BoxContent>
                                <div className="select-transport-box__left__boarding-arrival">
                                  {transport.detail(item)}
                                </div>
                              </Segment>
                            );
                          })
                      : item.transports.map((t, i) => (
                          <Segment key={i}>
                            <div className="flex">
                              <Name>{t.name}</Name>
                            </div>
                            <BoxContent>
                              <Space>発</Space>
                              <Space>{t.from.dateTimeStr()}</Space>
                              <Space>{t.from.name}</Space>
                            </BoxContent>
                            <BoxContent>
                              <Space>着</Space>
                              <Space>{t.to.dateTimeStr()}</Space>
                              <Space>{t.to.name}</Space>
                            </BoxContent>
                            <div className="select-transport-box__left__boarding-arrival">{t.detail()}</div>
                          </Segment>
                        ))}
                  </Left>
                  <Right>
                    <BoxPriceArea>
                      <div>
                        <BoxPrice>{utils.formatPriceWithMark(getOrderItemTotalPrice(item))}</BoxPrice>
                      </div>
                    </BoxPriceArea>
                  </Right>
                </Box>
              </Body>
            </>
          ) : null}
        </div>
      );
    } catch (e) {
      reportError(e);
      return null;
    }
  }
}

const Box = styled.div`
  display: flex;
  border: 2px solid ${props => props.theme.grayBorderColor};
  background: white;
  margin-bottom: 10px;
  font-size: 10px;
`;

const HotelBody = styled.div`
  padding-bottom: 5px;
`;

const HotelBodyText = styled.div``;

const bedTypeDisplayStyle = { whiteSpace: 'pre-line' as const };

const Right = styled.div`
  display: flex;
  flex-grow: 0;
  margin-left: auto;
  min-width: 170px;
  color: ${props => props.theme.linkColor};
  background: ${props => props.theme.grayBgColorLight};
`;

export default OrderItemBlock;
